<script setup lang="ts">
  import { computed, h } from 'vue'

  import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3'

  import { CalendarIcon, GraduationHat02Icon, Users01Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { cleanMentionText } from '@common/editor-layer/utils/mention-renderer'

  import { getChannelIcon, hasChannelIcon, isChannelIconEmoji } from '@communities/channels-layer/utils/channel.helper'

  const props = defineProps(nodeViewProps)

  const attrs = computed(() => {
    const {
      id = '',
      name = '',
      slug = '',
      icon = '',
      iconType = 'emoji',
      label = '',
      timestamp = null,
      type = 'channel',
    } = props.node.attrs || {}
    return { id, name, slug, icon, iconType, label, timestamp, type }
  })

  // Map internal type to data-type values
  const dataType = computed(() => {
    switch (attrs.value.type) {
      case 'channel':
        return 'channel-mention'
      case 'event':
        return 'events-mention'
      case 'course':
        return 'courses-mention'
      case 'user':
        return 'mention'
      case 'broadcast':
        return 'mention' // @everyone uses the same data-type as user mentions
      default:
        return 'channel-mention'
    }
  })

  // Same displayText / cleanedText / mentionText as renderMentionsAsChips (mention-renderer)
  const cleanText = computed(() => {
    const displayName = attrs.value.label || attrs.value.name || attrs.value.slug || ''
    return cleanMentionText(displayName)
  })

  const displayMentionText = computed(() => {
    const { type } = attrs.value
    const text = cleanText.value
    if ((type === 'user' || type === 'broadcast') && text) return `@${text}`
    return text
  })

  /** User/broadcast mentions use text-only chips; icon only for channel/event/course */
  const showMentionIcon = computed(() => attrs.value.type !== 'user' && attrs.value.type !== 'broadcast')

  const iconComponent = computed(() => {
    switch (attrs.value.type) {
      case 'user':
        return null
      case 'broadcast':
        return h(Users01Icon, { class: 'w-[18px] h-[18px]' })
      case 'event':
        return h(CalendarIcon, { class: 'w-[18px] h-[18px]' })
      case 'course':
        return h(GraduationHat02Icon, { class: 'w-[18px] h-[18px]' })
      case 'channel':
      default:
        // For channels, check if it has an emoji
        if (attrs.value.icon && isChannelIconEmoji(attrs.value.icon)) {
          return attrs.value.icon // Return emoji as string
        }
        // Check if it has an SVG icon
        if (attrs.value.icon && hasChannelIcon(attrs.value.icon)) {
          const IconComponent = getChannelIcon(attrs.value.icon)
          if (IconComponent) {
            return h(IconComponent, { class: 'w-[18px] h-[18px]' })
          }
        }
        // Fallback to hash symbol
        return '<span class="flex-shrink-0">#</span>'
    }
  })

  const handleRemove = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    props.deleteNode()
  }
</script>

<template>
  <NodeViewWrapper as="span" class="inline-block max-w-[100%]">
    <component
      :is="attrs.type === 'broadcast' ? 'span' : 'a'"
      :data-type="dataType"
      :data-mention-type="attrs.type"
      :data-id="attrs.id"
      :data-label="attrs.label"
      :data-name="attrs.name"
      :data-slug="attrs.slug"
      :data-icon="attrs.icon"
      :data-icon-type="attrs.iconType"
      :data-timestamp="attrs.timestamp"
      contenteditable="false"
      :href="attrs.type === 'broadcast' ? undefined : '#'"
      class="mention-editable max-w-[95%]"
    >
      <span v-if="showMentionIcon" class="mention-icon">
        <!-- VNode component (SVG icons) -->
        <component :is="iconComponent" v-if="iconComponent != null && typeof iconComponent === 'object'" />
        <!-- Emoji (simple string, no HTML tags) -->
        <span v-else-if="typeof iconComponent === 'string' && !iconComponent.includes('<')">{{ iconComponent }}</span>
        <!-- HTML string (hash fallback) -->
        <span v-else-if="iconComponent != null" v-html="iconComponent"></span>
      </span>
      <span class="mention-text truncate min-w-0">{{ displayMentionText }}</span>
      <XCloseIcon class="mention-remove-icon" @click="handleRemove" />
    </component>
  </NodeViewWrapper>
</template>

<style scoped>
  /* The mention styling comes from global CSS */
  /* We just add the remove icon styling here */

  .mention-remove-icon {
    @apply ml-1 h-4 w-4 cursor-pointer transition-colors;
    color: var(--cp-font-600); /* Always visible with the specified color */
  }

  .mention-remove-icon:hover {
    color: var(--cp-font-700);
  }

  /* Editor mentions inherit all styling from global CSS data-type selectors */
  /* Only add editor-specific styles here */
</style>
